import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import FilterDropdown, {FilterDropdownRef} from './FilterDropdown';
import FilterLayout from './FilterLayout';
import FilterMultiSelect from './FilterMultiSelect';
import {FilterOption} from '../../../common/types/FilterOption';
import placesService from '../../services/placesService';
import {handleError} from '../../../common/utils/errorHandling';
import './filters.scss';

interface PlacesFilterProps {
  places?: string[];
  onFilterChange: (places?: string[]) => void;
}

const PlacesFilter: React.FC<PlacesFilterProps> = ({
  places = [],
  onFilterChange
}) => {
  const {t} = useTranslation();
  const [tempPlaces, setTempPlaces] = useState<string[]>(places);
  const [placeOptions, setPlaceOptions] = useState<FilterOption[]>([]);
  const filterDropdownRef = useRef<FilterDropdownRef>(null);

  const hasActiveFilter = places.length > 0;

  useEffect(() => {
    fetchPlaces();
  }, []);

  const fetchPlaces = async () => {
    handleError(async () => {
      const placesData = await placesService.getAllPlaces();
      const options: FilterOption[] = placesData.map(place => ({
        label: place.name,
        value: place.id
      }));
      setPlaceOptions(options);
    }, 'Error fetching places');
  };

  const applyFilter = () => {
    const filteredPlaces = tempPlaces.length > 0 ? tempPlaces : undefined;
    onFilterChange(filteredPlaces);
  };

  const clearFilter = () => {
    setTempPlaces([]);
    onFilterChange(undefined);
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      applyFilter();
      filterDropdownRef.current?.close();
    } else if (event.key === 'Escape') {
      setTempPlaces(places);
      filterDropdownRef.current?.close();
    }
  };

  const handleApplyClick = () => {
    applyFilter();
    filterDropdownRef.current?.close();
  };

  const getDisplayLabel = () => {
    if (places.length === 0) {
      return t('gallery.filters.places');
    }
    return `${t('gallery.filters.places')} | ${places.length}`;
  };

  return (
    <FilterDropdown
      ref={filterDropdownRef}
      label={getDisplayLabel()}
      hasActiveFilter={hasActiveFilter}
      onClearFilter={clearFilter}
    >
      <FilterLayout
        title={t('gallery.filters.filterByPlaces')}
        onClear={clearFilter}
        onApply={handleApplyClick}
      >
        <FilterMultiSelect
          value={tempPlaces}
          options={placeOptions}
          onChange={setTempPlaces}
          onKeyDown={handleKeyPress}
          width={800}
          showFilter={true}
        />
      </FilterLayout>
    </FilterDropdown>
  );
};

export default PlacesFilter;
